import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { ArrowDownIcon, ArrowUpIcon, Trash2Icon, XIcon } from 'lucide-react'
import { VERAFIED_GUEST_STATUS_LABELS, type VerafiedEpisodeStatus, type VerafiedEvaluation, type VerafiedLinkState } from '@repo/types'
import { Alert, Button, Modal } from '@repo/ui'

import { Field, FormSubmitButton, SelectField, TextAreaField } from 'components'
import { ToggleField } from 'components/input/fields/ToggleField'
import { ConfirmSettingModal } from 'components/feedback/modals/ConfirmSettingModal/ConfirmSettingModal'
import { useSnackbar } from 'contexts'
import { useVerafiedActions, useVerafiedGuests, useVerafiedSegments } from 'hooks'
import type { VerafiedEpisode, VerafiedEpisodeInput } from 'services'

import { EPISODE_STATUS_OPTIONS, EVALUATION_OPTIONS, INDUSTRY_OPTIONS, lengthLabel, LINK_STATE_OPTIONS, parseLength, SOLUTION_OPTIONS } from './verafiedModel'

type Untyped = any

type EpisodeForm = {
  title: string
  description: string
  evaluation: string
  status: VerafiedEpisodeStatus
  solutions: string[]
  industries: string[]
  recording_on: string
  publish_on: string
  length: string
  video_url: string
  thumbnail_url: string
  has_trailer: boolean
  insights_slug: string
  claims_check: string
  notes: string
}

const blank = (v: string) => (v.trim() ? v.trim() : null)
const asList = (v: string | string[]) => (Array.isArray(v) ? v : v ? [v] : [])

/**
 * VRG-734 — add or edit a VERAfied episode. Guests and the running order are their own writes
 * (each link carries its own state), so they appear once the episode exists.
 */
export const EpisodeFormModal = ({
  open,
  episode,
  canManage,
  onClose,
}: {
  open: boolean
  episode: VerafiedEpisode | null
  canManage: boolean
  onClose: () => void
}) => {
  const { open: notify } = useSnackbar()
  const actions = useVerafiedActions()
  const guests = useVerafiedGuests()
  const segments = useVerafiedSegments()
  const methods = useForm<EpisodeForm>({ mode: 'onChange' })
  const { control, reset, handleSubmit } = methods
  const [confirmDelete, setConfirmDelete] = useState(false)
  const readOnly = !canManage

  useEffect(() => {
    if (!open) return
    reset({
      title: episode?.title ?? '',
      description: episode?.description ?? '',
      evaluation: episode?.evaluation ?? '',
      status: episode?.status ?? 'idea',
      solutions: episode?.solutions ?? [],
      industries: episode?.industries ?? [],
      recording_on: episode?.recording_on ?? '',
      publish_on: episode?.publish_on ?? '',
      length: lengthLabel(episode?.length_seconds ?? null) ?? '',
      video_url: episode?.video_url ?? '',
      thumbnail_url: episode?.thumbnail_url ?? '',
      has_trailer: episode?.has_trailer ?? false,
      insights_slug: episode?.insights_slug ?? '',
      claims_check: episode?.claims_check ?? '',
      notes: episode?.notes ?? '',
    })
    // Re-seed only when a different episode opens, so a guest link written here does not wipe edits.
  }, [open, episode?.id, reset]) // eslint-disable-line react-hooks/exhaustive-deps

  const linkedIds = useMemo(() => new Set((episode?.guests ?? []).map((g) => g.id)), [episode])
  const addableGuests = useMemo(
    () => (guests.data ?? []).filter((g) => !linkedIds.has(g.id)).map((g) => ({ value: g.id, label: g.name })),
    [guests.data, linkedIds],
  )
  const order = useMemo(() => [...(episode?.segments ?? [])].sort((a, b) => a.position - b.position), [episode])
  const addableSegments = useMemo(
    () => (segments.data ?? []).filter((s) => !order.some((o) => o.id === s.id)).map((s) => ({ value: s.id, label: s.name })),
    [segments.data, order],
  )

  const fail = (e: unknown) => notify({ type: 'error', text: (e as Error).message })
  const setOrder = (ids: string[]) => episode && actions.setEpisodeSegments.mutate({ episodeId: episode.id, segmentIds: ids }, { onError: fail })
  const moveSegment = (i: number, by: -1 | 1) => {
    const ids = order.map((s) => s.id)
    const j = i + by
    if (j < 0 || j >= ids.length) return
    ;[ids[i], ids[j]] = [ids[j], ids[i]]
    setOrder(ids)
  }

  const save = handleSubmit((v) => {
    const length = parseLength(v.length)
    if (length === 'invalid') {
      methods.setError('length', { message: 'Minutes, or minutes:seconds' })
      return
    }
    const input: VerafiedEpisodeInput = {
      title: v.title.trim(),
      description: blank(v.description),
      evaluation: (blank(v.evaluation) as VerafiedEvaluation | null) ?? null,
      status: v.status,
      solutions: v.solutions,
      industries: v.industries,
      recording_on: blank(v.recording_on),
      publish_on: blank(v.publish_on),
      length_seconds: length,
      video_url: blank(v.video_url),
      thumbnail_url: blank(v.thumbnail_url),
      has_trailer: v.has_trailer,
      insights_slug: blank(v.insights_slug),
      claims_check: blank(v.claims_check),
      notes: blank(v.notes),
    }
    const done = () => {
      onClose()
    }
    if (episode) actions.updateEpisode.mutate({ id: episode.id, input }, { onSuccess: done, onError: fail })
    else actions.createEpisode.mutate(input, { onSuccess: done, onError: fail })
  })

  const m = methods as Untyped
  const saving = actions.createEpisode.isPending || actions.updateEpisode.isPending

  const select = (name: keyof EpisodeForm, label: string, options: Array<{ value: string; label: string }>, multiple = false) => (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <SelectField
          label={label}
          multiple={multiple}
          placeholder={multiple ? 'None' : 'Not set'}
          options={options}
          value={field.value as string | string[]}
          disabled={readOnly}
          onChange={(v) => field.onChange(multiple ? asList(v) : String(v))}
        />
      )}
    />
  )

  return (
    <>
      <Modal
        title={episode ? (readOnly ? episode.title : 'Edit episode') : 'Add episode'}
        open={open}
        onClose={onClose}
        sx={{ width: 720 }}
        actions={
          readOnly ? (
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          ) : (
            <>
              {episode && (
                <Button
                  variant="secondary"
                  destructive="hover"
                  startIcon={<Trash2Icon size={18} strokeWidth={1} />}
                  onClick={() => setConfirmDelete(true)}
                  sx={{ mr: 'auto' }}
                >
                  Delete episode
                </Button>
              )}
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <FormSubmitButton control={control} requireDirty={!!episode} onClick={() => save()} loading={saving}>
                {episode ? 'Save' : 'Add episode'}
              </FormSubmitButton>
            </>
          )
        }
      >
        <Stack spacing={4} mt={2}>
          {episode?.claims_check && (
            // The check text names itself ("Claims check: …"), so the alert carries it alone.
            <Alert severity="warning" description={episode.claims_check} />
          )}
          {episode && episode.import_notes.length > 0 && (
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              From the import: {episode.import_notes.join(' ')}
            </Typography>
          )}
          <Field {...m} name="title" label="Title" readOnly={readOnly} rules={{ required: 'Give the episode a title' }} />
          <TextAreaField {...m} name="description" label="One-line description (optional)" minRows={2} />
          <Stack direction="row" spacing={3}>
            <Box sx={{ flex: 1 }}>{select('evaluation', 'Evaluation', [{ value: '', label: 'Unrated' }, ...EVALUATION_OPTIONS])}</Box>
            <Box sx={{ flex: 1 }}>{select('status', 'Status', EPISODE_STATUS_OPTIONS)}</Box>
          </Stack>
          <Stack direction="row" spacing={3}>
            <Box sx={{ flex: 1, minWidth: 0 }}>{select('solutions', 'Solutions', SOLUTION_OPTIONS, true)}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>{select('industries', 'Industries', INDUSTRY_OPTIONS, true)}</Box>
          </Stack>
          <Stack direction="row" spacing={3}>
            <Box sx={{ flex: 1 }}>
              <Field {...m} name="recording_on" label="Recording date" type="date" readOnly={readOnly} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Field {...m} name="publish_on" label="Publish date" type="date" readOnly={readOnly} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Field {...m} name="length" label="Length" placeholder="42:30" readOnly={readOnly} />
            </Box>
          </Stack>
          <Stack direction="row" spacing={3}>
            <Box sx={{ flex: 1 }}>
              <Field {...m} name="video_url" label="Video file (optional)" placeholder="https://" readOnly={readOnly} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Field {...m} name="thumbnail_url" label="Thumbnail (optional)" placeholder="https://" readOnly={readOnly} />
            </Box>
          </Stack>
          <Stack direction="row" spacing={3} alignItems="flex-start">
            <Box sx={{ flex: 2 }}>
              <Field {...m} name="insights_slug" label="/insights slug (once published)" placeholder="the-resale-revolution" readOnly={readOnly} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Controller
                control={control}
                name="has_trailer"
                render={({ field }) => (
                  <ToggleField label="Has a trailer" labelVariant="body3" checked={!!field.value} onChange={(c) => !readOnly && field.onChange(c)} />
                )}
              />
            </Box>
          </Stack>

          {episode && (
            <Stack spacing={2}>
              <Typography variant="body3" sx={{ fontWeight: 500 }}>
                Guests
              </Typography>
              {episode.guests.length === 0 && (
                <Typography variant="body3" color="text.secondary">
                  No guests linked yet.
                </Typography>
              )}
              {episode.guests.map((g) => (
                <Stack key={g.id} direction="row" spacing={3} alignItems="center">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body3" noWrap>
                      {g.name}
                    </Typography>
                    <Typography variant="body3" color="text.secondary">
                      Guest status: {VERAFIED_GUEST_STATUS_LABELS[g.status]}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 170 }}>
                    <SelectField
                      options={LINK_STATE_OPTIONS}
                      value={g.state}
                      disabled={readOnly}
                      onChange={(v) =>
                        actions.setEpisodeGuest.mutate(
                          { episodeId: episode.id, guestId: g.id, state: String(v) as VerafiedLinkState },
                          { onError: fail },
                        )
                      }
                    />
                  </Box>
                  {!readOnly && (
                    <Tooltip title="Unlink from this episode">
                      <IconButton
                        aria-label={`Unlink ${g.name}`}
                        onClick={() => actions.removeEpisodeGuest.mutate({ episodeId: episode.id, guestId: g.id }, { onError: fail })}
                      >
                        <XIcon size={16} strokeWidth={1} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              ))}
              {!readOnly && addableGuests.length > 0 && (
                <Box sx={{ maxWidth: 320 }}>
                  <SelectField
                    placeholder="Link a guest"
                    options={addableGuests}
                    value=""
                    onChange={(v) =>
                      actions.setEpisodeGuest.mutate({ episodeId: episode.id, guestId: String(v), state: 'candidate' }, { onError: fail })
                    }
                  />
                </Box>
              )}
            </Stack>
          )}

          {episode && (
            <Stack spacing={2}>
              <Typography variant="body3" sx={{ fontWeight: 500 }}>
                Running order
              </Typography>
              {order.length === 0 && (
                <Typography variant="body3" color="text.secondary">
                  No segments yet. Pick from the Formats library.
                </Typography>
              )}
              {order.map((s, i) => (
                <Stack key={s.id} direction="row" spacing={2} alignItems="center">
                  <Typography variant="body3" color="text.secondary" sx={{ width: 20 }}>
                    {i + 1}
                  </Typography>
                  <Typography variant="body3" sx={{ flex: 1 }}>
                    {s.name}
                  </Typography>
                  {!readOnly && (
                    <>
                      <IconButton aria-label={`Move ${s.name} up`} disabled={i === 0} onClick={() => moveSegment(i, -1)}>
                        <ArrowUpIcon size={16} strokeWidth={1} />
                      </IconButton>
                      <IconButton aria-label={`Move ${s.name} down`} disabled={i === order.length - 1} onClick={() => moveSegment(i, 1)}>
                        <ArrowDownIcon size={16} strokeWidth={1} />
                      </IconButton>
                      <IconButton aria-label={`Remove ${s.name}`} onClick={() => setOrder(order.filter((o) => o.id !== s.id).map((o) => o.id))}>
                        <XIcon size={16} strokeWidth={1} />
                      </IconButton>
                    </>
                  )}
                </Stack>
              ))}
              {!readOnly && addableSegments.length > 0 && (
                <Box sx={{ maxWidth: 320 }}>
                  <SelectField
                    placeholder="Add a segment"
                    options={addableSegments}
                    value=""
                    onChange={(v) => setOrder([...order.map((o) => o.id), String(v)])}
                  />
                </Box>
              )}
            </Stack>
          )}
          {!episode && (
            <Typography variant="body3" color="text.secondary">
              Link guests and set the running order once the episode is added.
            </Typography>
          )}

          <TextAreaField
            {...m}
            name="claims_check"
            label="Claims check (optional)"
            placeholder="A figure in the title or description that needs a source before it goes public"
            minRows={2}
          />
          <TextAreaField {...m} name="notes" label="Notes (internal)" minRows={2} />
        </Stack>
      </Modal>
      <ConfirmSettingModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={`Delete ${episode?.title ?? 'this episode'}?`}
        consequence="This deletes the episode, its guest links and its running order. The guests themselves stay."
        confirmLabel="Delete episode"
        loading={actions.deleteEpisode.isPending}
        onConfirm={() => {
          if (!episode) return
          actions.deleteEpisode.mutate(episode.id, {
            onSuccess: () => {
              setConfirmDelete(false)
              onClose()
            },
            onError: fail,
          })
        }}
      />
    </>
  )
}
